import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { MessageSquareText, Plus, Trash2, Loader2, Sparkles, FolderOpen } from "lucide-react";
import { LANGS } from "@/lib/langs";
import { pollJob } from "@/lib/pollJob";
import { AnswersPanel } from "./screening/AnswersPanel";

export default function Screening() {
  const rid = () => Math.random().toString(36).slice(2);
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [language, setLanguage] = useState("en");
  const [questions, setQuestions] = useState([{ id: rid(), text: "" }]);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState(null);
  const [applications, setApplications] = useState([]);
  const [applicationId, setApplicationId] = useState("");
  const [savedSets, setSavedSets] = useState([]);
  const [currentSetId, setCurrentSetId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAux = async () => {
    const [apps, sets] = await Promise.all([
      api.get("/applications").catch(() => ({ data: [] })),
      api.get("/screening-sets").catch(() => ({ data: [] })),
    ]);
    setApplications(apps.data);
    setSavedSets(sets.data);
  };

  useEffect(() => {
    api.get("/settings").then(({ data }) => setLanguage(data.preferred_language || "en")).catch(() => {});
    loadAux();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setQ = (id, v) => setQuestions((qs) => qs.map((q) => (q.id === id ? { ...q, text: v } : q)));
  const addQ = () => setQuestions((qs) => [...qs, { id: rid(), text: "" }]);
  const removeQ = (id) => setQuestions((qs) => (qs.length === 1 ? qs : qs.filter((q) => q.id !== id)));

  const generate = async () => {
    const qs = questions.map((q) => q.text.trim()).filter(Boolean);
    if (qs.length === 0) { toast.error("Add at least one question"); return; }
    setLoading(true);
    try {
      const { data: job } = await api.post("/screening/generate", {
        job_title: jobTitle, company, job_description: jobDesc, questions: qs, language,
      });
      const data = await pollJob(job.job_id, "/screening/jobs");
      setAnswers((data.answers || []).map((a) => ({ ...a, _k: rid() })));
      setCurrentSetId(null);
      toast.success("Answers generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const saveSet = async () => {
    if (!answers || answers.length === 0) return;
    setSaving(true);
    try {
      const payload = {
        job_title: jobTitle, company, job_description: jobDesc,
        answers: answers.map(({ _k, ...rest }) => rest), application_id: applicationId || null,
      };
      if (currentSetId) {
        await api.put(`/screening-sets/${currentSetId}`, payload);
        toast.success("Answer set updated");
      } else {
        const { data } = await api.post("/screening-sets", payload);
        setCurrentSetId(data.screening_set_id);
        toast.success("Answer set saved");
      }
      loadAux();
    } catch (e) {
      toast.error("Could not save answer set");
    } finally {
      setSaving(false);
    }
  };

  const loadSet = (s) => {
    setJobTitle(s.job_title || "");
    setCompany(s.company || "");
    setJobDesc(s.job_description || "");
    setAnswers((s.answers || []).map((a) => ({ ...a, _k: rid() })));
    setQuestions((s.answers || []).map((a) => ({ id: rid(), text: a.question })));
    setApplicationId(s.application_id || "");
    setCurrentSetId(s.id);
    toast.success("Loaded saved set");
  };

  const deleteSet = async (id) => {
    await api.delete(`/screening-sets/${id}`);
    if (currentSetId === id) setCurrentSetId(null);
    toast.success("Deleted");
    loadAux();
  };

  const appLabel = (id) => {
    const a = applications.find((x) => x.id === id);
    return a ? `${a.job_title} · ${a.company}` : null;
  };

  const pickApplication = (v) => {
    if (v === "none") { setApplicationId(""); return; }
    setApplicationId(v);
    const a = applications.find((x) => x.id === v);
    if (a) {
      setJobTitle(a.job_title || "");
      setCompany(a.company || "");
      if (a.job_description) setJobDesc(a.job_description);
      const linkedSet = savedSets.find((s) => s.application_id === v && (s.answers || []).length > 0);
      if (linkedSet) {
        setQuestions(linkedSet.answers.map((ans) => ({ id: rid(), text: ans.question })));
        setAnswers(null);
        setCurrentSetId(null);
        toast.success(`Prefilled from application · loaded ${linkedSet.answers.length} question(s) from a saved set`);
      } else {
        toast.success("Prefilled from application");
      }
    }
  };

  const updateAnswer = (i, v) => setAnswers((a) => a.map((x, idx) => (idx === i ? { ...x, answer: v } : x)));

  const copyOne = (text) => {
    try {
      const p = navigator.clipboard?.writeText(text);
      if (p && p.then) {
        p.then(() => toast.success("Copied")).catch(() => toast.error("Copy failed"));
      } else {
        toast.success("Copied");
      }
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const downloadAll = () => {
    const text = answers.map((a) => `Q: ${a.question}\n\n${a.answer}`).join("\n\n---\n\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url; el.download = `screening_answers_${company || "job"}.txt`;
    document.body.appendChild(el); el.click(); el.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="screening-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Screening Answers</h1>
        <p className="text-muted-foreground mt-1 text-sm">Generate tailored answers to application screening questions from your profile.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4 space-y-4">
          <Card className="border-zinc-200 shadow-sm">
            <CardHeader><CardTitle className="font-heading text-xl">Context</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Job Title</Label>
                <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Senior Engineer" data-testid="screening-jobtitle-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Company</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Inc." data-testid="screening-company-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Job Description (optional)</Label>
                <Textarea rows={6} value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} placeholder="Paste the job description…" data-testid="screening-jobdesc-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Link to Application (optional)</Label>
                <Select value={applicationId || "none"} onValueChange={pickApplication}>
                  <SelectTrigger data-testid="screening-application-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {applications.map((a) => <SelectItem key={a.id} value={a.id}>{a.job_title} · {a.company}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Selecting an application fills in the job title, company and description.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Output Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="screening-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGS.map((l) => <SelectItem key={l.value} value={l.value} data-testid={`screening-lang-${l.value}`}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Overrides your Settings language for these answers only.</p>
              </div>
            </CardContent>
          </Card>

          {savedSets.length > 0 && (
            <Card className="border-zinc-200 shadow-sm">
              <CardHeader><CardTitle className="font-heading text-xl">Saved Sets</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {savedSets.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 p-2.5" data-testid={`saved-set-${s.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.job_title || "Untitled"} · {s.company || "—"}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {s.answers?.length || 0} answers{s.application_id && appLabel(s.application_id) ? ` · ${appLabel(s.application_id)}` : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => loadSet(s)} data-testid={`load-set-${s.id}`}><FolderOpen className="h-4 w-4" strokeWidth={1.5} /></Button>
                      <ConfirmDelete
                        onConfirm={() => deleteSet(s.id)}
                        triggerTestId={`delete-set-${s.id}`}
                        confirmTestId={`confirm-delete-set-${s.id}`}
                        title="Delete this answer set?"
                        description={`"${s.job_title || "Untitled"} · ${s.company || "—"}" will be permanently removed.`}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="font-heading text-xl">Questions</CardTitle>
              <Button variant="ghost" size="sm" onClick={addQ} data-testid="add-question-button"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add</Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {questions.map((q, i) => (
                <div key={q.id} className="flex gap-2 items-start">
                  <Textarea rows={2} value={q.text} onChange={(e) => setQ(q.id, e.target.value)} placeholder={`Question ${i + 1}`} data-testid={`question-input-${i}`} />
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => removeQ(q.id)} data-testid={`remove-question-${i}`}>
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              ))}
              <Button onClick={generate} disabled={loading} data-testid="generate-answers-button" className="w-full h-11 active:scale-[0.98] transition-transform">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Generate Answers</>}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="col-span-12 lg:col-span-8">
          {!answers && (
            <div className="h-full min-h-[400px] rounded-md border border-dashed border-zinc-300 bg-white flex flex-col items-center justify-center text-center p-8" data-testid="screening-empty">
              <MessageSquareText className="h-10 w-10 text-zinc-300 mb-3" strokeWidth={1.25} />
              <p className="font-medium">Your answers appear here</p>
              <p className="text-sm text-muted-foreground mt-1">Add questions and click Generate Answers.</p>
            </div>
          )}

          {answers && (
            <AnswersPanel
              answers={answers}
              saving={saving}
              saveSet={saveSet}
              currentSetId={currentSetId}
              downloadAll={downloadAll}
              updateAnswer={updateAnswer}
              copyOne={copyOne}
            />
          )}
        </div>
      </div>
    </div>
  );
}
